/**
 * The session (§8). The screen a patient actually does their therapy on.
 *
 * THE SHAPE OF A SESSION, and the friction budget it has to fit inside
 * (constraint 4 — a 10-minute prescription runs in 11 or 12 minutes, with about
 * two seconds of the patient's attention spent answering questions):
 *
 *   delayed-onset check   one tap, skipped on a first session
 *   pre-session check     one tap; a red answer ends the day
 *   transition            per exercise: what is next, how to sit or stand, a
 *                         countdown, and ONE TAP that is also the rating
 *   the exercise          full screen, stop always visible, spacebar stops
 *   close-out             one tap; the 0-10 form only if something happened
 *
 * The transition screen doing two jobs is the whole reason the budget works. It
 * reads as a transition rather than an interruption, and the rating costs
 * nothing on top of it.
 *
 * WHAT THIS SCREEN NEVER DOES. It never lets the patient choose a session
 * (constraint 11 — the server decides). It never shows a score, a streak, a
 * completion percentage, or a comparison with a previous day (constraint 3). It
 * never describes a stop as incomplete or as a target missed: stopping early is
 * a correct clinical decision and §9 Layer 6 makes the wording part of the
 * safety system.
 *
 * WHERE THE SAFETY RULES LIVE. Not in this component. Whether today runs at all
 * and at what dose is the server's (`safety/gate.ts`); whether a session that is
 * under way should end is `decide.ts`, next door, because it has to answer on a
 * tap without a round trip. This file asks them and carries out what they say.
 * A rule written inline here would be a rule with no test, and these are the
 * rules whose failure is silent.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDefinition, type MetricSample, type StopReason } from '@pathways/exercises';
import {
  api,
  type RunExercise,
  type StartRunResponse,
  type SymptomRating,
  type TherapyState,
} from '../../api.js';
import { ExerciseStage, type ExerciseStageHandle } from '../../components/ExerciseStage.js';
import { TRANSITION_SEC } from '../clinician/timing.js';
import { recordResult } from './recorder.js';
import { sessionOverran, symptomVerdict, type StopCause } from './decide.js';
import { DecayMonitor, type DecayTrace } from './decay.js';
import {
  BLOCKED_RISEN_TODAY,
  BLOCKED_TODAY,
  closeOutMessage,
  POSITION_SETUP,
  RATING_OPTIONS,
  RATING_QUESTION,
  REDUCED_TODAY,
  SAFETY_STOP_ACKNOWLEDGEMENT,
  STOP_ACKNOWLEDGEMENT,
  SYMPTOM_TYPES,
  isStandingPosition,
  safetyStopHeading,
  stopHeading,
} from './copy.js';
import styles from './SessionRunner.module.css';

type Phase =
  | 'loading'
  | 'delayed_onset'
  | 'pre_session'
  | 'pre_detail'
  | 'blocked'
  | 'transition'
  | 'running'
  | 'stopped'
  /** Ended by a safety rule rather than by the patient (§9 Layers 2 and 3). */
  | 'safety_stopped'
  | 'close_out'
  | 'close_detail'
  | 'done'
  | 'unavailable';

export function SessionRunner() {
  const navigate = useNavigate();
  const stageRef = useRef<ExerciseStageHandle | null>(null);

  const [phase, setPhase] = useState<Phase>('loading');
  const [run, setRun] = useState<StartRunResponse | null>(null);
  const [index, setIndex] = useState(0);
  const [pxPerDegree, setPxPerDegree] = useState<number | null>(null);

  const [delayedOnset, setDelayedOnset] = useState<SymptomRating | null>(null);
  /** Whether the first question was asked at all — Back needs to know. */
  const [askedDelayedOnset, setAskedDelayedOnset] = useState(false);
  /** Kept so coming back to the pre-session question shows what was tapped. */
  const [preSession, setPreSession] = useState<SymptomRating | null>(null);
  const [pendingRating, setPendingRating] = useState<SymptomRating | null>(null);
  const [closeOut, setCloseOut] = useState<SymptomRating | null>(null);
  const [countdown, setCountdown] = useState(TRANSITION_SEC);
  /**
   * What is still due today, read back from the server after the run is
   * finished. The aim is the patient's own prescription and they are told it;
   * the ceiling is never sent here, so `moreAllowed` is a boolean and nothing
   * on this screen can count down to it.
   */
  const [afterwards, setAfterwards] = useState<{
    remainingToAim: number;
    moreAllowed: boolean;
  }>({ remainingToAim: 0, moreAllowed: false });

  /** Every rating given today, so the close-out knows whether to ask for detail. */
  const ratingsRef = useRef<SymptomRating[]>([]);
  /** True once the patient has stopped anything: the session ends, not just the exercise. */
  const stoppedEarlyRef = useRef(false);
  /**
   * TODAY'S BASELINE — the pre-session answer, and the only thing every
   * in-session rule is measured against (§9 Layer 2: rules fire on change from
   * today, never on absolute values, and never on yesterday).
   */
  const baselineRef = useRef<SymptomRating>(1);
  /** Why a safety rule ended the session, for the record and for the wording. */
  const safetyCauseRef = useRef<StopCause | null>(null);
  /** When the first exercise began, for the whole-session ceiling. */
  const sessionStartedAtRef = useRef<number | null>(null);

  const samplesRef = useRef<MetricSample[]>([]);
  const deliveredRef = useRef<Record<string, number> | null>(null);
  const startedAtRef = useRef<string>('');
  /**
   * §9 Layer 3. One monitor per exercise, built when the exercise starts and
   * thrown away when it ends — the baseline is within-session by design, and
   * carrying one across exercises would compare a patient's saccades against
   * their pursuits.
   */
  const monitorRef = useRef<DecayMonitor | null>(null);
  /** What the monitor saw when it stopped something, for the record. */
  const decayTraceRef = useRef<DecayTrace | null>(null);

  const exercises = run?.exercises ?? [];
  const current: RunExercise | undefined = exercises[index];
  const definition = current ? getDefinition(current.exerciseId) : undefined;

  // --- Getting started -------------------------------------------------------

  /**
   * A session URL reached when no session can start — a tab left open since
   * this morning, or a bookmark. The patient is sent back to their own home
   * screen, which already says WHY in their own words: the day's work is done,
   * or the gap has not passed, or there is no program yet.
   *
   * Redirecting rather than explaining here is deliberate. Those are three
   * clinical messages, and a second copy of them on this screen would be free
   * to drift from the first — which for §9 Layer 6 copy is not a cosmetic
   * problem.
   */
  useEffect(() => {
    if (phase === 'unavailable') navigate('/therapy/home', { replace: true });
  }, [phase, navigate]);

  useEffect(() => {
    let cancelled = false;
    api
      .get<TherapyState>('/therapy/session')
      .then((state) => {
        if (cancelled) return;
        if (!state.calibration || state.today.state !== 'ready') {
          setPhase('unavailable');
          return;
        }
        setPxPerDegree(state.calibration.pxPerDegree);
        setAskedDelayedOnset(state.askDelayedOnset);
        setPhase(state.askDelayedOnset ? 'delayed_onset' : 'pre_session');
      })
      .catch(() => {
        if (!cancelled) setPhase('unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const startRun = useCallback(
    async (pre: SymptomRating, detail?: Record<string, number>) => {
      try {
        const started = await api.post<StartRunResponse>('/therapy/runs', {
          delayedOnsetRating: delayedOnset,
          preSessionRating: pre,
          symptomDetail: detail ?? null,
        });
        setRun(started);
        baselineRef.current = pre;
        /*
          Both answers are recorded HERE rather than as they were tapped, now
          that they can be changed. Pushed at tap time, going back and
          answering again would leave both answers behind and the close-out
          would think something had happened when nothing had.
        */
        if (delayedOnset !== null) ratingsRef.current.push(delayedOnset);
        ratingsRef.current.push(pre);
        if (started.blocked) {
          setPhase('blocked');
          return;
        }
        setIndex(0);
        setCountdown(TRANSITION_SEC);
        setPhase('transition');
      } catch {
        // A 409 means the day moved underneath this screen — already run
        // elsewhere, or the program withdrawn. Nothing here is worth retrying,
        // and the home screen will say what the actual situation is.
        setPhase('unavailable');
      }
    },
    [delayedOnset],
  );

  /**
   * §7's single-exercise ceiling. This one DOES fire mid-exercise, unlike the
   * whole-session limit: it exists to catch one exercise prescribed far longer
   * than intended, and waiting for it to finish would be waiting for exactly
   * the thing it is meant to prevent.
   *
   * A backstop, not a dose. In an ordinary prescription it never fires, because
   * the exercise's own duration ends it first.
   */
  useEffect(() => {
    if (phase !== 'running' || !run?.safety) return;
    const timer = window.setTimeout(
      () => stageRef.current?.stop('safety_duration'),
      run.safety.maxExerciseMinutes * 60_000,
    );
    return () => window.clearTimeout(timer);
  }, [phase, run, index]);

  // --- §9 Layer 4: the rest between exercises is on a timer -----------------
  // Not a "continue when ready" button. A motivated patient presses continue
  // immediately, every time, which is exactly the population this is for.
  useEffect(() => {
    if (phase !== 'transition') return;
    if (countdown <= 0) return;
    const timer = window.setTimeout(() => setCountdown((n) => n - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [phase, countdown]);

  // --- Ending a session -----------------------------------------------------

  /**
   * SEAL THE RUN THE MOMENT IT ENDS EARLY, before the patient has answered
   * anything else.
   *
   * Everything in §9 Layer 5 hangs off this call — the safety event, the
   * same-day lockout, the dose reduction, the progression lock. If it waited
   * for the close-out, a patient who closed the tab on the stop screen would
   * take none of it with them: no event, no lockout, and the session that just
   * went wrong repeating unchanged. That is the exact failure Layer 5 exists to
   * prevent, arriving through the front door.
   *
   * The close-out rating still lands afterwards; the finish route takes a
   * rating on an already-finished run and nothing else.
   */
  const sealRun = useCallback(
    (runId: string, cause: StopCause | 'patient_stop', exerciseId: string | null) => {
      void api
        .post(`/therapy/runs/${runId}/finish`, {
          outcome: cause === 'patient_stop' ? 'STOPPED_BY_PATIENT' : 'STOPPED_BY_SAFETY',
          stopReason: cause,
          exerciseId,
          trace: decayTraceRef.current,
        })
        .catch(() => {
          // Offline. The close-out will try again with the same cause, and the
          // exercise results are buffered and retried independently.
        });
    },
    [],
  );

  /**
   * End the session because a rule said so, not because the patient pressed
   * stop (§9 Layer 5 rule 1: the REST of today's session ends, not just the
   * exercise that was next).
   *
   * Everything already done today is recorded and stays recorded — results are
   * written per exercise for exactly this reason. What ends is what has not
   * happened yet.
   */
  const endForSafety = useCallback(
    (cause: StopCause) => {
      safetyCauseRef.current = cause;
      stoppedEarlyRef.current = true;
      if (run) sealRun(run.runId, cause, current?.exerciseId ?? null);
      setPhase('safety_stopped');
    },
    [run, current, sealRun],
  );

  // --- Recording -------------------------------------------------------------

  const finishExercise = useCallback(
    async (reason: StopReason) => {
      if (!run || !current) return;

      const completed = reason === 'completed';
      await recordResult(run.runId, {
        exerciseInstanceId: current.instanceId,
        exerciseId: current.exerciseId,
        exerciseVersion: current.exerciseVersion,
        orderIndex: current.orderIndex,
        position: current.position,
        inputMode: current.inputMode,
        paramsUsed: current.params,
        deliveredParams: deliveredRef.current,
        // The first exercise's rating is the pre-session check, taken moments
        // earlier; asking again would be friction for a second copy of the
        // same answer.
        symptomRating: index === 0 ? null : pendingRating,
        startedAt: startedAtRef.current,
        endedAt: new Date().toISOString(),
        completed,
        stopReason: completed ? null : reason,
        samples: samplesRef.current,
      });

      // Recorded here rather than when the button was tapped, so a patient who
      // changes their mind before starting leaves one answer behind, not two.
      if (index > 0 && pendingRating !== null) ratingsRef.current.push(pendingRating);

      samplesRef.current = [];
      deliveredRef.current = null;

      if (reason === 'patient_stop') {
        stoppedEarlyRef.current = true;
        // Sealed now, for the same reason a safety stop is: §9 lists the
        // patient's own stop first among the triggers, and it must not depend
        // on them staying on the screen that follows it.
        sealRun(run.runId, 'patient_stop', current.exerciseId);
        setPhase('stopped');
        return;
      }

      /**
       * §9 Layer 5 rule 1: when a safety trigger fires, the REST of today's
       * session ends — not just the exercise that fired it. Carrying on to the
       * next exercise after the monitor found the patient degrading is the
       * behaviour this whole layer exists to prevent.
       *
       * The result for what they DID manage has already been written above.
       */
      if (reason === 'safety_decay' || reason === 'safety_duration') {
        endForSafety(reason);
        return;
      }
      if (index + 1 >= exercises.length) {
        setPhase('close_out');
        return;
      }

      /**
       * §7's whole-session ceiling, checked HERE rather than mid-exercise.
       * Stopping an exercise part-way to satisfy a session limit would throw
       * away a measurement in order to enforce a backstop; the single-exercise
       * cap is what stops one exercise running long, and that one does fire
       * while it runs.
       */
      if (
        run.safety &&
        sessionStartedAtRef.current !== null &&
        sessionOverran(sessionStartedAtRef.current, Date.now(), run.safety)
      ) {
        endForSafety('session_duration');
        return;
      }

      setIndex(index + 1);
      setPendingRating(null);
      setCountdown(TRANSITION_SEC);
      setPhase('transition');
    },
    [run, current, index, exercises.length, pendingRating, endForSafety, sealRun],
  );

  /**
   * End the patient's session on this device.
   *
   * The logout is attempted and not waited on for correctness: if it fails, the
   * cookie is still there but the server expires it after 90 minutes idle
   * anyway, and holding a patient who has just finished on a spinner because a
   * request is slow is the wrong trade. The navigation happens either way.
   */
  const signOut = useCallback(async () => {
    await api.post('/therapy/logout').catch(() => undefined);
    navigate('/therapy', { replace: true });
  }, [navigate]);

  const finishSession = useCallback(
    async (rating: SymptomRating | null, detail?: Record<string, number>) => {
      if (run) {
        try {
          /**
           * A safety stop and a patient stop are different records even though
           * they look the same to the patient. Which one it was is the first
           * thing the doctor needs to know: one is the patient recognising
           * something and acting on it, the other is the software acting for
           * them, and the clinical reading of the two is not the same.
           */
          const cause = safetyCauseRef.current;
          await api.post(`/therapy/runs/${run.runId}/finish`, {
            outcome: cause
              ? 'STOPPED_BY_SAFETY'
              : stoppedEarlyRef.current
                ? 'STOPPED_BY_PATIENT'
                : 'COMPLETED',
            stopReason: cause ?? (stoppedEarlyRef.current ? 'patient_stop' : null),
            closeOutRating: rating,
            symptomDetail: detail ?? null,
            // Which exercise was on screen, and what the monitor saw. §9
            // requires the doctor to be able to see exactly what happened,
            // and a stop with no trace attached is a stop he has to take on
            // trust.
            exerciseId: current?.exerciseId ?? null,
            trace: decayTraceRef.current,
          });
        } catch {
          // The exercises themselves are already recorded and buffered if need
          // be. A close-out that could not be delivered is not worth holding a
          // symptomatic patient on a screen for.
        }
      }
      // Ask the server what is left rather than working it out here: it owns
      // the schedule, and a second implementation would be free to disagree
      // with it about what a patient still owes.
      try {
        const state = await api.get<TherapyState>('/therapy/session');
        setAfterwards({
          remainingToAim:
            state.today.state === 'ready' || state.today.state === 'waiting'
              ? state.today.remainingToAim
              : 0,
          moreAllowed: state.today.state === 'ready' || state.today.state === 'waiting',
        });
      } catch {
        // Offline. The close-out says the plain thing rather than guessing at
        // what is still due.
      }
      setPhase('done');
    },
    [run],
  );

  // --- Screens ---------------------------------------------------------------

  if (phase === 'loading') return <main className={styles.page} />;

  // Nothing is rendered for this: the home screen already explains every reason
  // a session cannot start — the ceiling reached, the gap not yet passed, no
  // published program, access ended — in the patient's own words. Restating any
  // of that here would be a second copy of three clinical messages, free to
  // drift from the first. See the redirect above.
  if (phase === 'unavailable') return <main className={styles.page} />;

  if (phase === 'delayed_onset') {
    return (
      <RatingCard
        question={RATING_QUESTION.delayedOnset}
        note="The hours afterwards tell us more than the session itself does."
        chosen={delayedOnset}
        onAnswer={(rating) => {
          setDelayedOnset(rating);
          setPhase('pre_session');
        }}
        onBack={() => navigate('/therapy/home')}
      />
    );
  }

  if (phase === 'pre_session') {
    return (
      <RatingCard
        question={RATING_QUESTION.preSession}
        chosen={preSession}
        onAnswer={(rating) => {
          setPreSession(rating);
          // §8: red means the session does not run today. The detail form is
          // worth its cost here precisely because no session follows it.
          if (rating === 3) setPhase('pre_detail');
          else void startRun(rating);
        }}
        onBack={
          askedDelayedOnset
            ? () => setPhase('delayed_onset')
            : () => navigate('/therapy/home')
        }
      />
    );
  }

  if (phase === 'pre_detail') {
    return (
      <DetailCard
        onSubmit={(detail) => {
          void startRun(3, detail);
        }}
        /*
          The most important Back on the patient side. Nothing has been written
          yet — the run is created when this form is submitted — so a mis-tapped
          "Not good" is still completely undoable at this point, and after it
          is not: it ends the day and goes on the record.
        */
        onBack={() => setPhase('pre_session')}
      />
    );
  }

  if (phase === 'blocked') {
    // Two different reasons and two different messages. A patient who has
    // already worked today and is being told to stop has done nothing wrong,
    // and being handed the same words as someone who was too symptomatic to
    // begin would read as a rebuke.
    const message = run?.blockedReason === 'within_day_rise' ? BLOCKED_RISEN_TODAY : BLOCKED_TODAY;
    return (
      <Card
        heading={message.heading}
        body={message.body}
        action={{ label: 'Done', onClick: () => navigate('/therapy/home', { replace: true }) }}
      />
    );
  }

  if (phase === 'stopped') {
    const position = current?.position ?? 'seated';
    return (
      <Card
        heading={stopHeading(position)}
        body={STOP_ACKNOWLEDGEMENT}
        emphasis={isStandingPosition(position)}
        action={{ label: 'Continue', onClick: () => setPhase('close_out') }}
      />
    );
  }

  if (phase === 'safety_stopped') {
    // §9: the fall risk leads whatever ended the session. Someone standing when
    // it stopped is still standing now.
    const position = current?.position ?? 'seated';
    return (
      <Card
        heading={safetyStopHeading(position)}
        body={SAFETY_STOP_ACKNOWLEDGEMENT}
        emphasis={isStandingPosition(position)}
        action={{ label: 'Continue', onClick: () => setPhase('close_out') }}
      />
    );
  }

  if (phase === 'close_out') {
    return (
      <RatingCard
        question={RATING_QUESTION.closeOut}
        chosen={closeOut}
        onAnswer={(rating) => {
          setCloseOut(rating);
          /*
            §8: "the detailed form appears only when something actually
            happened". Everything green means everything green — no form.

            This answer is NOT pushed into `ratingsRef`, and that matters now
            that it can be changed: pushed, a patient who answered "not good",
            went back, and answered "good" would still be asked for detail
            because their first answer was sitting in the list. It is read
            directly instead, so the question is always about the answer they
            are actually giving.
          */
          const anythingHappened =
            stoppedEarlyRef.current || rating !== 1 || ratingsRef.current.some((r) => r !== 1);
          if (anythingHappened) setPhase('close_detail');
          else void finishSession(rating);
        }}
      />
    );
  }

  if (phase === 'close_detail') {
    return (
      <DetailCard
        onSubmit={(detail) => void finishSession(closeOut, detail)}
        onBack={() => setPhase('close_out')}
      />
    );
  }

  if (phase === 'done') {
    const message = closeOutMessage(afterwards.remainingToAim, afterwards.moreAllowed);
    return (
      <Card
        heading={message.heading}
        body={message.body}
        /*
          FINISH SIGNS OUT. The day's work is over, and leaving a patient on a
          screen offering another session is a promise the software may not
          keep: a patient session goes idle after 90 minutes and the default gap
          between sessions is two hours, so anyone coming back for a second one
          is entering their code again regardless.

          Nothing is lost by it. The confirmation they need — that today's
          therapy is done and the doctor will see it — is on THIS screen, above
          the button.
        */
        action={{ label: 'Finish', onClick: () => void signOut() }}
        /*
          §2 of the close-out: the answer is already written by the time this
          screen appears, so this is not going back — it is changing something
          on the record, and the label says so. A wrong close-out rating is a
          wrong clinical fact on a chart that is the only instrument there is.
        */
        secondary={{ label: 'Change my answer', onClick: () => setPhase('close_out') }}
      />
    );
  }

  // --- The exercise ----------------------------------------------------------
  // The stage is mounted for the transition too, with the transition card laid
  // over it. Entering full screen needs a user gesture AND an element that is
  // already on the page, so pressing start has to reach a stage that exists.

  if (!current || !definition || pxPerDegree === null) {
    return (
      <Card
        heading="Not right now"
        body="This exercise could not be loaded. Please tell Dr. Ramirez."
        action={{ label: 'Back', onClick: () => navigate('/therapy/home', { replace: true }) }}
      />
    );
  }

  const ready = countdown <= 0 && (index === 0 || pendingRating !== null);

  return (
    <div className={styles.screen}>
      <ExerciseStage
        key={current.instanceId}
        ref={stageRef}
        definition={definition}
        params={current.params}
        position={current.position}
        inputMode={current.inputMode}
        pxPerDegree={pxPerDegree}
        fullScreen
        exitStopReason="patient_stop"
        idleHint=""
        onSample={(sample) => {
          /*
            THE SEAM (docs/ARCHITECTURE.md). One call from the exercise, fanned
            out to the recorder and to the safety monitor. The exercise knows
            about neither, which is why the monitor needs no exercise-specific
            code and why a new exercise is watched the day it is registered.
          */
          samplesRef.current.push(sample);

          const reading = monitorRef.current?.observe(sample);
          if (reading?.kind === 'stop') {
            decayTraceRef.current = reading.trace;
            // Stopping the STAGE, not the session directly: the exercise has to
            // shut its run loop down and leave full screen, and the result for
            // what it did manage still has to be written. `finishExercise`
            // picks the session up from there.
            stageRef.current?.stop('safety_decay');
          }
        }}
        onDelivered={(values) => {
          deliveredRef.current = values;
        }}
        onStopped={(reason) => void finishExercise(reason)}
      >
        {phase === 'running' && (
          /* §8: large, persistent, on every exercise screen. No confirmation
             dialog, no "are you sure" — it stops immediately. Inside the stage
             because full screen shows nothing outside it. */
          <button
            type="button"
            className={styles.stopButton}
            onClick={() => stageRef.current?.stop('patient_stop')}
          >
            Stop
            <span className={styles.stopHint}>or press the spacebar</span>
          </button>
        )}
      </ExerciseStage>

      {phase === 'transition' && (
        <div className={styles.overlay}>
          <div className={styles.transition}>
            <p className={styles.next}>
              Next: {definition.name}
              {exercises.length > 1 && (
                <span className={styles.ofTotal}>
                  {' '}
                  ({index + 1} of {exercises.length})
                </span>
              )}
            </p>

            {/*
              Today is running short, said once at the start rather than on
              every transition. A patient who is not told reads a shorter
              session as the app having lost their program; a patient told six
              times is being reminded six times that something is wrong with
              them. No number and no reason — see REDUCED_TODAY.
            */}
            {index === 0 && run?.reducedDose && (
              <div className={styles.reduced}>
                <strong className={styles.reducedHeading}>{REDUCED_TODAY.heading}</strong>
                <span>{REDUCED_TODAY.body}</span>
              </div>
            )}

            <p className={styles.instruction}>{definition.patientInstruction}</p>

            <div
              className={
                isStandingPosition(current.position) ? styles.positionAlert : styles.position
              }
            >
              <strong className={styles.positionHeading}>
                {POSITION_SETUP[current.position].heading}
              </strong>
              <span>{POSITION_SETUP[current.position].body}</span>
            </div>

            {current.patientNote && <p className={styles.note}>{current.patientNote}</p>}

            {index > 0 && (
              <fieldset className={styles.ratingInline}>
                <legend className={styles.ratingLegend}>{RATING_QUESTION.transition}</legend>
                <div className={styles.ratingOptions}>
                  {RATING_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={
                        pendingRating === option.value ? styles.ratingChosen : styles.rating
                      }
                      onClick={() => setPendingRating(option.value)}
                    >
                      <span className={styles.ratingLabel}>{option.label}</span>
                      <span className={styles.ratingHint}>{option.hint}</span>
                    </button>
                  ))}
                </div>
              </fieldset>
            )}

            <button
              type="button"
              className={styles.begin}
              disabled={!ready}
              onClick={() => {
                /**
                 * §9 Layer 2, decided HERE and synchronously.
                 *
                 * Entering full screen requires a user gesture, and anything
                 * awaited between this tap and `start()` spends it — the
                 * exercise would then open in a window with the stop button
                 * somewhere off the screen the patient is looking at. So the
                 * rule cannot be a request. See `decide.ts`.
                 *
                 * The rating has not been written anywhere yet: it normally
                 * rides along with the exercise result, and there is no result
                 * when the exercise never starts. Recorded here instead so the
                 * answer that ended the session is not the one answer missing
                 * from the record of it.
                 */
                if (index > 0 && pendingRating !== null && run?.safety) {
                  const verdict = symptomVerdict(
                    baselineRef.current,
                    pendingRating,
                    null,
                    run.safety,
                  );
                  if (verdict.kind === 'stop') {
                    ratingsRef.current.push(pendingRating);
                    endForSafety(verdict.cause);
                    return;
                  }
                }

                startedAtRef.current = new Date().toISOString();
                if (sessionStartedAtRef.current === null) {
                  sessionStartedAtRef.current = Date.now();
                }
                samplesRef.current = [];
                decayTraceRef.current = null;
                // §9 Layer 3, on unless the doctor has turned it off for this
                // patient. Built here so its baseline starts with the exercise.
                monitorRef.current = run?.safety?.decayMonitorEnabled
                  ? new DecayMonitor(definition.decay, run.safety)
                  : null;
                setPhase('running');
                void stageRef.current?.start();
              }}
            >
              {countdown > 0
                ? `Take a moment — ${countdown}`
                : index === 0
                  ? 'Begin'
                  : pendingRating === null
                    ? 'Answer above to continue'
                    : 'Begin'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// The small screens
// =============================================================================

function Card({
  heading,
  body,
  action,
  secondary,
  emphasis,
}: {
  heading: string;
  body: string;
  action: { label: string; onClick: () => void };
  /** A quiet second option below the main one. Used for changing an answer. */
  secondary?: { label: string; onClick: () => void };
  emphasis?: boolean;
}) {
  return (
    <main className={styles.page}>
      <section className={styles.card}>
        <h1 className={emphasis ? styles.headingAlert : styles.heading}>{heading}</h1>
        <p className={styles.body}>{body}</p>
        <button type="button" className={styles.begin} onClick={action.onClick}>
          {action.label}
        </button>
        {secondary && (
          <button type="button" className={styles.back} onClick={secondary.onClick}>
            {secondary.label}
          </button>
        )}
      </section>
    </main>
  );
}

/**
 * The 3-point scale (§8). One tap, three options, no scale to interpret and no
 * numbers on screen — a number invites a patient to compare today with
 * yesterday, which is the doctor's job and not theirs.
 */
function RatingCard({
  question,
  note,
  chosen,
  onAnswer,
  onBack,
}: {
  question: string;
  note?: string;
  /** The answer already given, when the patient has come back to change it. */
  chosen?: SymptomRating | null;
  onAnswer: (rating: SymptomRating) => void;
  /** Absent where there is nothing to go back to. */
  onBack?: () => void;
}) {
  return (
    <main className={styles.page}>
      <section className={styles.card}>
        <h1 className={styles.heading}>{question}</h1>
        {note && <p className={styles.body}>{note}</p>}
        <div className={styles.ratingOptions}>
          {RATING_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={chosen === option.value ? styles.ratingChosen : styles.rating}
              onClick={() => onAnswer(option.value)}
            >
              <span className={styles.ratingLabel}>{option.label}</span>
              <span className={styles.ratingHint}>{option.hint}</span>
            </button>
          ))}
        </div>
        {/*
          A wrong tap on these questions is not a small thing. "Not good" ends
          the day and goes on the record as a safety event, and before this
          there was no way back from one — the screen simply moved on.

          Quiet and below the answers, not a third large button beside them:
          it must be findable without competing with the question, and it must
          never read as though changing an answer were a correction of the
          patient.
        */}
        {onBack && (
          <button type="button" className={styles.back} onClick={onBack}>
            Back
          </button>
        )}
      </section>
    </main>
  );
}

/**
 * The 0-10 detail, which §8 opens only when something actually happened. Fixed
 * symptom types and no free-text field anywhere — §2 is explicit that
 * de-identification erodes through free text, and this is the field a patient
 * would type their own name into.
 */
function DetailCard({
  onSubmit,
  onBack,
}: {
  onSubmit: (detail: Record<string, number>) => void;
  onBack?: () => void;
}) {
  const [values, setValues] = useState<Record<string, number>>({});

  return (
    <main className={styles.page}>
      <section className={styles.card}>
        <h1 className={styles.heading}>What is bothering you?</h1>
        <p className={styles.body}>
          Zero means none at all. Leave anything you are not feeling at zero.
        </p>

        {SYMPTOM_TYPES.map((symptom) => (
          <div key={symptom.key} className={styles.symptom}>
            <label className={styles.symptomLabel} htmlFor={`symptom-${symptom.key}`}>
              {symptom.label}
            </label>
            <input
              id={`symptom-${symptom.key}`}
              className={styles.symptomSlider}
              type="range"
              min={0}
              max={10}
              step={1}
              value={values[symptom.key] ?? 0}
              onChange={(e) =>
                setValues((previous) => ({ ...previous, [symptom.key]: Number(e.target.value) }))
              }
            />
            <output className={styles.symptomValue}>{values[symptom.key] ?? 0}</output>
          </div>
        ))}

        <button type="button" className={styles.begin} onClick={() => onSubmit(values)}>
          Done
        </button>
        {onBack && (
          <button type="button" className={styles.back} onClick={onBack}>
            Back
          </button>
        )}
      </section>
    </main>
  );
}
